import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialWidgets } from '@/hooks/use-financial-widgets';
import { Skeleton } from '@/components/ui/skeleton';

interface WarrantyStatusCardProps {
  readonly className?: string;
}

interface CustomTooltipProps {
  readonly active?: boolean;
  readonly payload?: Array<{ payload: { name: string; value: number; percentage: number } }>;
}

const CustomTooltipComponent = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold">{data.name}</p>
        <p className="text-sm text-muted-foreground">
          {data.value} assets ({data.percentage.toFixed(1)}%)
        </p>
      </div>
    );
  }
  return null;
};

export function WarrantyStatusCard({ className }: WarrantyStatusCardProps): React.ReactElement {
  const { useWarrantyStatus } = useFinancialWidgets();
  const { data, isLoading, error } = useWarrantyStatus();

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Warranty Status</CardTitle>
          <CardDescription>Asset warranty coverage overview</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.categories.length === 0) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Warranty Status</CardTitle>
          <CardDescription>Asset warranty coverage overview</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No warranty data available.</div>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Warranty Status</CardTitle>
            <CardDescription>Asset warranty coverage overview</CardDescription>
          </div>
          <div className="p-2 bg-green-50 dark:bg-green-950 rounded-lg">
            <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {/* Summary Stats */}
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="text-center p-4 rounded-lg border bg-card">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">{data.activeWarranties}</div>
            <div className="text-sm text-muted-foreground mt-1">Active Warranties</div>
          </div>
          <div className="text-center p-4 rounded-lg border bg-card">
            <div className="text-3xl font-bold">{data.totalAssets.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground mt-1">Total Assets</div>
          </div>
        </div>

        {/* Warranty Status Cards */}
        <div className="space-y-3">
          {/* Active */}
          <div className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-4 h-4 rounded-sm flex-shrink-0 bg-green-500" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">Active</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {((data.activeWarranties / data.totalAssets) * 100).toFixed(1)}% of total assets
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {data.activeWarranties}
                </div>
                <div className="text-xs text-muted-foreground">assets</div>
              </div>
            </div>
            <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-green-500"
                style={{ width: `${(data.activeWarranties / data.totalAssets) * 100}%` }}
              />
            </div>
          </div>

          {/* Expiring Soon (30 days) */}
          {data.expiringWithin30Days > 0 && (
            <div className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-4 h-4 rounded-sm flex-shrink-0 bg-orange-500" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Expiring in 30 Days</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {((data.expiringWithin30Days / data.totalAssets) * 100).toFixed(1)}% of total assets
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {data.expiringWithin30Days}
                  </div>
                  <div className="text-xs text-muted-foreground">assets</div>
                </div>
              </div>
              <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-orange-500"
                  style={{ width: `${(data.expiringWithin30Days / data.totalAssets) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Expiring Soon (90 days) */}
          {data.expiringWithin90Days > 0 && (
            <div className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-4 h-4 rounded-sm flex-shrink-0 bg-yellow-500" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Expiring in 90 Days</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {((data.expiringWithin90Days / data.totalAssets) * 100).toFixed(1)}% of total assets
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {data.expiringWithin90Days}
                  </div>
                  <div className="text-xs text-muted-foreground">assets</div>
                </div>
              </div>
              <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-yellow-500"
                  style={{ width: `${(data.expiringWithin90Days / data.totalAssets) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Expired */}
          {data.expiredWarranties > 0 && (
            <div className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-4 h-4 rounded-sm flex-shrink-0 bg-red-500" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">Expired</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {((data.expiredWarranties / data.totalAssets) * 100).toFixed(1)}% of total assets
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {data.expiredWarranties}
                  </div>
                  <div className="text-xs text-muted-foreground">assets</div>
                </div>
              </div>
              <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-red-500"
                  style={{ width: `${(data.expiredWarranties / data.totalAssets) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* No Warranty */}
          {data.noWarranty > 0 && (
            <div className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-4 h-4 rounded-sm flex-shrink-0 bg-gray-400" />
                  <div className="flex-1">
                    <div className="font-semibold text-sm">No Warranty</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {((data.noWarranty / data.totalAssets) * 100).toFixed(1)}% of total assets
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                    {data.noWarranty}
                  </div>
                  <div className="text-xs text-muted-foreground">assets</div>
                </div>
              </div>
              <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-gray-400"
                  style={{ width: `${(data.noWarranty / data.totalAssets) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
